import { Flex } from '@chakra-ui/layout'
import { useToast } from '@chakra-ui/toast'
import {
  addEdge,
  Background, Connection, Edge, ReactFlow, ReactFlowProvider, useEdgesState, useNodesState
} from '@xyflow/react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FaSave } from 'react-icons/fa'
import { MdRestartAlt } from 'react-icons/md'
import 'reactflow/dist/style.css'
import { ButtonCmp } from '../../../../../../components/ButtonCmp'
import { ModalDelete } from '../../../../../../components/ModalDelete'
import { iNoOrganograma, iOrganogramaCompleto, IDepartment } from '../../../../../../interfaces'
import { appApi } from '../../../../../../services/appApi'
import { parseJwt } from '../../../../../../services/token'
import { buildReactFlowGraph, iPosicaoTopo } from './BuildReactFlowGraph'
import CustomEdge from './CustomEdge'
import CustomNode from './CustomNode'

const nodeTypes = {
  custom: CustomNode
}

const edgeTypes = {
  custom: CustomEdge
}

interface OrganogramaProps {
  departamentos: IDepartment[]
  cores: string[]
  layoutPersistido?: boolean
  podeEditarLigacoes?: boolean
  aoSalvarHierarquia?: () => void
  endpoint?: string
  ordenavel?: boolean
}

interface iAlteracaoHierarquia {
  departamentoId: string
  departamentoPaiId: string | null
}

const Organograma: React.FC<OrganogramaProps> = ({ departamentos, cores, layoutPersistido = true, podeEditarLigacoes = false, aoSalvarHierarquia, endpoint = 'OrganogramaNode', ordenavel = false }) => {
  const toast = useToast()
  const posicoesRef = useRef<Record<string, Record<string, iPosicaoTopo>>>({})
  const [VersaoPosicoes, setVersaoPosicoes] = useState(0)
  const [Id, setId] = useState<string>()
  const [savedNodes, setSavedNodes] = useState<iNoOrganograma[]>()
  const [ModalRestaurarAberto, setModalRestaurarAberto] = useState(false)
  const [ModalHierarquiaAberto, setModalHierarquiaAberto] = useState(false)

  const ehAdministrador = parseJwt().adm === 'True'
  const podeSalvarLayout = ehAdministrador && layoutPersistido
  const temLayoutSalvo = Id !== undefined || savedNodes !== undefined

  // Quando já tiver carregado os dados do backend
  const { nodes: initialNodes, edges: initialEdges } = useMemo(() => {
    return buildReactFlowGraph(departamentos, cores, savedNodes)
  }, [departamentos, cores, savedNodes, VersaoPosicoes])

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes as any)
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges as any)

  const onMoverTopo = useCallback((squadId: string, colaboradorId: string, posicao: iPosicaoTopo) => {
    posicoesRef.current = {
      ...posicoesRef.current,
      [squadId]: { ...posicoesRef.current[squadId], [colaboradorId]: posicao }
    }
    setNodes((nos: any[]) => nos.map(no => no.id === squadId
      ? { ...no, data: { ...no.data, posicoes: posicoesRef.current[squadId] } }
      : no
    ))
  }, [])

  // Cada área tem um pai só: a ligação nova substitui a que já chegava no alvo.
  const onConnect = useCallback(
    (params: Edge | Connection) => {
      if (params.source === params.target) return
      setEdges((els) => {
        if (els.some((edge) => edge.source === params.source && edge.target === params.target)) return els
        return addEdge(params, els.filter((edge) => edge.target !== params.target))
      })
    },
    [setEdges]
  )

  const onExcluirLigacao = useCallback(
    (id: string) => setEdges((els) => els.filter((edge) => edge.id !== id)),
    [setEdges]
  )

  const ligacoes = useMemo(() => edges.map((edge: any) => ({
    ...edge,
    type: 'custom',
    deletable: podeEditarLigacoes,
    data: {
      ...edge.data,
      podeExcluir: podeEditarLigacoes,
      onExcluir: onExcluirLigacao
    }
  })), [edges, onExcluirLigacao, podeEditarLigacoes])

  function calcularAlteracoesDeHierarquia(): iAlteracaoHierarquia[] {
    const paiNoCadastro = new Map<string, string>()
    const registrarFilhos = (departamento: IDepartment): void => {
      departamento.departamentoFilhos?.forEach(filho => {
        paiNoCadastro.set(filho.id, departamento.id)
        registrarFilhos(filho)
      })
    }
    departamentos.forEach(registrarFilhos)

    const paiNaTela = new Map<string, string>(edges.map((edge: any) => [edge.target, edge.source]))

    return nodes
      .map((node: any) => node.id as string)
      .filter(id => (paiNoCadastro.get(id) ?? null) !== (paiNaTela.get(id) ?? null))
      .map(id => ({ departamentoId: id, departamentoPaiId: paiNaTela.get(id) ?? null }))
  }

  function onSalvar(): void {
    if (!podeEditarLigacoes || calcularAlteracoesDeHierarquia().length === 0) {
      onPost()
      return
    }
    setModalHierarquiaAberto(true)
  }

  function onSalvarHierarquia(): void {
    appApi.put('Departamento/Hierarquia', { alteracoes: calcularAlteracoesDeHierarquia() })
      .then(() => onPost(aoSalvarHierarquia))
      .catch(err => {
        const mensagem = err?.response?.data?.mensagem
        if (err?.response?.status === 400 && mensagem) {
          toast({ title: mensagem, status: 'error', position: 'top', duration: 6000 })
        } else if (err?.response?.status === 403) {
          toast({ title: 'Você não possui permissão para alterar a hierarquia das áreas', status: 'error', position: 'top', duration: 6000 })
        } else {
          console.log(err)
        }
      })
  }

  function onPost(aoConcluir?: () => void): void {
    const idsNaTela = new Set(nodes.map((node: any) => node.id))
    const nosPreservados = savedNodes?.filter(no => !idsNaTela.has(no.data?.departamentoId)) ?? []

    const data = {
      id: Id,
      empresaId: parseJwt().eid,
      nodes: [
        ...nodes.map((node: any) => ({
          id: node.id,
          type: node.type,
          data: { departamentoId: node.data?.departamentoId ?? node.id },
          position: node.position,
          width: node.width ?? node.measured?.width ?? 930,
          height: node.height ?? node.measured?.height ?? 245
        })),
        ...nosPreservados
      ],
      // o back ainda exige o campo, as ligações vêm do cadastro
      edges: [],
      ...(ordenavel && {
        posicoes: Object.entries(posicoesRef.current).map(([squadId, colaboradores]) => ({
          squadId,
          colaboradores: Object.entries(colaboradores).map(([colaboradorId, posicao]) => ({ colaboradorId, ...posicao }))
        }))
      })
    }
    appApi.post(endpoint, data).then(() => {
      setSavedNodes(data.nodes)
      toast({
        title: 'Organograma salvo com sucesso',
        status: 'success',
        position: 'top',
        duration: 4000
      })
    }).catch(err => console.log(err))
      .finally(() => aoConcluir?.())
  }

  function onRestaurarLayoutPadrao(): void {
    appApi.delete(endpoint).then(() => {
      posicoesRef.current = {}
      setVersaoPosicoes(versao => versao + 1)
      setId(undefined)
      setSavedNodes(undefined)
      toast({
        title: 'Layout padrão restaurado com sucesso',
        status: 'success',
        position: 'top',
        duration: 4000
      })
    }).catch(err => console.log(err))
  }

  function onGet(): void {
    appApi.get(endpoint).then(res => {
      if (res.status === 200 && res.data) {
        const data: iOrganogramaCompleto = res.data
        posicoesRef.current = Object.fromEntries((data.posicoes ?? []).map(item => [
          item.squadId,
          Object.fromEntries(item.colaboradores.map(c => [c.colaboradorId, { x: c.x, y: c.y }]))
        ]))
        setVersaoPosicoes(versao => versao + 1)
        setId(data.id)
        setSavedNodes(data.nodes)
      }
    }).catch(err => console.log(err))
  }

  useEffect(() => {
    if (podeSalvarLayout) {
      onGet()
    }
  }, [])

  // UseEffect para atualizar o estado do ReactFlow quando mudar os departamentos ou os dados salvos
  useEffect(() => {
    setNodes((ordenavel
      ? initialNodes.map(no => ({ ...no, data: { ...no.data, posicoes: posicoesRef.current[no.id], onMoverTopo } }))
      : initialNodes) as any)
    setEdges(initialEdges)
  }, [initialNodes, initialEdges])

  return (
    <Flex flexDir={'column'} gap={'1rem'} alignItems={'end'}>
      {podeSalvarLayout && <Flex gap={'.5rem'} mr={'2rem'}>
        {temLayoutSalvo && <ButtonCmp
          VarColor='c5'
          leftIcon={<MdRestartAlt />}
          onClick={() => setModalRestaurarAberto(true)}
        >
          Restaurar layout padrão
        </ButtonCmp>}
        <ButtonCmp
          VarColor='Rosa'
          leftIcon={<FaSave />}
          w={'12rem'}
          onClick={onSalvar}
        >
          Salvar organograma
        </ButtonCmp>
      </Flex>}

      <ModalDelete
        isOpen={ModalHierarquiaAberto}
        onRequestClose={() => setModalHierarquiaAberto(false)}
        onConfirm={onSalvarHierarquia}
        center
        message={'As alterações serão gravadas no cadastro de áreas e podem mudar a visibilidade dos gestores sobre as áreas subordinadas. Deseja continuar?'}
      />

      <ModalDelete
        isOpen={ModalRestaurarAberto}
        onRequestClose={() => setModalRestaurarAberto(false)}
        onConfirm={onRestaurarLayoutPadrao}
        center
        message={ordenavel
          ? 'O layout personalizado e a posição dos colaboradores serão descartados e a visualização voltará a ser gerada automaticamente pelo cadastro de squads. Deseja continuar?'
          : 'O layout personalizado do organograma será descartado e a visualização voltará a ser gerada automaticamente pelo cadastro de áreas. Deseja continuar?'}
      />
      <ReactFlowProvider>
        <ReactFlow
          nodes={nodes}
          edges={ligacoes}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          nodesConnectable={podeEditarLigacoes}
          nodeTypes={nodeTypes as any}
          edgeTypes={edgeTypes as any}
          minZoom={0.05}
          fitView
          style={{
            minWidth: '95vw',
            maxWidth: '95vw',
            minHeight: '93vh',
            maxHeight: '93vh',
            border: '1px solid var(--c6)'
          }}
          defaultEdgeOptions={{
            style: { stroke: '#1961E0', strokeWidth: 2 }
          }}
        >
          <Background />
        </ReactFlow>
      </ReactFlowProvider>
    </Flex>
  )
}

export default Organograma
